import * as React from 'react';
import { PaginatedSelectValue } from './PaginatedSelectValue';
import {
  PaginatedSelectLabelVariants,
  PaginatedSelectHelperTextVariants,
  PaginatedSelectErrorTextVariants,
  PaginatedSelectTriggerVariants,
} from './paginated-select.variants';
import { usePaginatedSelectContext } from './usePaginatedSelectContext';
import { cn } from '@/shared/lib/cn';

interface PaginatedSelectTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  size?: 'default' | 'sm' | 'lg';
  outline?: boolean;
}

function PaginatedSelectTrigger({
  className,
  size = 'default',
  outline = true,
  ...props
}: PaginatedSelectTriggerProps) {
  const ctx = usePaginatedSelectContext();
  const {
    open,
    setOpen,
    disabled,
    label,
    error,
    helperText,
    mode,
    items,
    renderItem,
    value,
    placeholder,
  } = ctx as {
    open: boolean;
    setOpen: (open: boolean) => void;
    disabled: boolean;
    label?: string;
    error?: string;
    helperText?: string;
    mode: 'single' | 'multiple';
    items: Array<Record<string, unknown>>;
    renderItem: (item: Record<string, unknown>) => { value: string; label: string };
    value: string | string[];
    placeholder?: string;
  };

  const selectedLabels = React.useMemo(() => {
    if (mode === 'multiple') {
      const selectedValues = Array.isArray(value) ? value : [];
      return selectedValues
        .map((val) => {
          const item = items.find((item) => renderItem(item).value === val);
          return item ? renderItem(item).label : '';
        })
        .filter(Boolean);
    }
    const item = items.find((item) => renderItem(item).value === value);
    return item ? [renderItem(item).label] : [];
  }, [mode, items, renderItem, value]);

  return (
    <div className="w-full space-y-1">
      {label && <label className={cn(PaginatedSelectLabelVariants())}>{label}</label>}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          PaginatedSelectTriggerVariants({
            size,
            outline: outline && !error,
            disabled,
            class: className,
          }),
          error && 'border-destructive',
          'bg-select-background',
        )}
        {...props}
      >
        <PaginatedSelectValue
          selectedLabels={selectedLabels}
          mode={mode}
          placeholder={placeholder}
        />
        <svg
          className="h-4 w-4 opacity-50 transition-transform duration-200"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {error && <p className={cn(PaginatedSelectErrorTextVariants())}>{error}</p>}
      {!error && helperText && (
        <p className={cn(PaginatedSelectHelperTextVariants())}>{helperText}</p>
      )}
    </div>
  );
}

export { PaginatedSelectTrigger };
